/**
 * Loads Split/Harness flag *definitions* (splitChanges) without initializing
 * the browser SDK. Used when analytics consent is denied so 100% released
 * flags still resolve, without getTreatment / impressions.
 */

export const SPLIT_CHANGES_URL =
  'https://sdk.split.io/api/splitChanges?since=-1'

const SPLIT_CHANGES_TIMEOUT_MS = 3000

/** AbortSignal.timeout is unavailable in our TS 4.5 DOM typings / older runtimes. */
function createTimeoutSignal(ms: number): AbortSignal {
  const controller = new AbortController()
  setTimeout(() => controller.abort(), ms)

  return controller.signal
}

export const RELEASED_TREATMENTS_STORAGE_KEY = 'tw_split_released_treatments'

export type SplitMatcher = {
  matcherType?: string
  keySelector?: { attribute?: string | null } | null
}

export const DEFAULT_RULE_LABEL = 'default rule'

export type SplitCondition = {
  conditionType?: string
  label?: string
  matcherGroup?: {
    matchers?: SplitMatcher[]
  }
  partitions?: Array<{ treatment: string; size: number }>
}

export type SplitDefinition = {
  name: string
  status?: string
  killed?: boolean
  defaultTreatment?: string
  conditions?: SplitCondition[]
  configurations?: Record<string, string>
}

type CachedReleasedTreatments = {
  till?: number
  treatments: Record<string, string>
  configs?: Record<string, string>
}

type SettlementListener = () => void

let releasedTreatments: Record<string, string> = {}
let releasedConfigs: Record<string, string> = {}
let rolloutPlanSettled = false
let inFlightFetch: Promise<Record<string, string>> | null = null
const settlementListeners = new Set<SettlementListener>()

function isAllKeysMatcher(matcher?: SplitMatcher): boolean {
  return (
    matcher?.matcherType === 'ALL_KEYS' &&
    (matcher.keySelector?.attribute == null ||
      matcher.keySelector?.attribute === undefined)
  )
}

function isDefaultRolloutRule(condition?: SplitCondition): boolean {
  if (!condition || condition.conditionType !== 'ROLLOUT') {
    return false
  }

  const matchers = condition.matcherGroup?.matchers ?? []

  return matchers.length === 1 && isAllKeysMatcher(matchers[0])
}

function isLabeledDefaultRule(condition?: SplitCondition): boolean {
  return (
    condition?.label === DEFAULT_RULE_LABEL && isDefaultRolloutRule(condition)
  )
}

function findDefaultRule(
  conditions: SplitCondition[]
): SplitCondition | undefined {
  const labeled = conditions.filter(isLabeledDefaultRule)

  if (labeled.length) {
    return labeled[labeled.length - 1]
  }

  const unlabeledAllKeys = conditions.filter(isDefaultRolloutRule)

  if (unlabeledAllKeys.length === 1) {
    return unlabeledAllKeys[0]
  }

  return undefined
}

function winnerFromPartitions(
  partitions: Array<{ treatment: string; size: number }> | undefined
): string | undefined {
  if (!partitions?.length) {
    return undefined
  }

  const hundredPercent = partitions.filter(
    (partition) => partition.size === 100
  )
  const othersAreZero = partitions.every(
    (partition) => partition.size === 0 || partition.size === 100
  )

  if (hundredPercent.length === 1 && othersAreZero) {
    return hundredPercent[0].treatment
  }

  return undefined
}

export function parseReleasedTreatments(
  splits: SplitDefinition[]
): Record<string, string> {
  const released: Record<string, string> = {}

  for (const split of splits) {
    if (!split?.name || split.status !== 'ACTIVE') {
      continue
    }

    if (split.killed) {
      if (split.defaultTreatment) {
        released[split.name] = split.defaultTreatment
      }

      continue
    }

    const defaultRule = findDefaultRule(split.conditions ?? [])
    const winner = winnerFromPartitions(defaultRule?.partitions)

    if (winner) {
      released[split.name] = winner
    }
  }

  return released
}

/**
 * Returns a map of flag name -> released config JSON string for 100%-released
 * flags that carry a configuration. Only includes entries where a config string
 * actually exists in the splitChanges payload — flags without configurations
 * are omitted so callers can fall back to '{}' cleanly.
 *
 * Mirrors the winner-resolution logic in parseReleasedTreatments so the two
 * functions always agree on which treatment is "the winner".
 */
export function parseReleasedConfigs(
  splits: SplitDefinition[]
): Record<string, string> {
  const configs: Record<string, string> = {}

  for (const split of splits) {
    if (!split?.name || split.status !== 'ACTIVE') {
      continue
    }

    let winnerTreatment: string | undefined

    if (split.killed) {
      winnerTreatment = split.defaultTreatment
    } else {
      const defaultRule = findDefaultRule(split.conditions ?? [])
      winnerTreatment = winnerFromPartitions(defaultRule?.partitions)
    }

    if (winnerTreatment) {
      const config = split.configurations?.[winnerTreatment]

      if (config != null) {
        configs[split.name] = config
      }
    }
  }

  return configs
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.values(value as Record<string, unknown>).every(
      (v) => typeof v === 'string'
    )
  )
}

function readSessionCache(): {
  treatments: Record<string, string>
  configs: Record<string, string>
} | null {
  if (typeof sessionStorage === 'undefined') {
    return null
  }

  try {
    const raw = sessionStorage.getItem(RELEASED_TREATMENTS_STORAGE_KEY)

    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw) as CachedReleasedTreatments

    if (!parsed?.treatments || typeof parsed.treatments !== 'object') {
      return null
    }

    return {
      treatments: parsed.treatments,
      configs: isStringRecord(parsed.configs) ? parsed.configs : {},
    }
  } catch {
    return null
  }
}

function writeSessionCache(
  treatments: Record<string, string>,
  configs: Record<string, string>,
  till?: number
): void {
  if (typeof sessionStorage === 'undefined') {
    return
  }

  try {
    sessionStorage.setItem(
      RELEASED_TREATMENTS_STORAGE_KEY,
      JSON.stringify({ till, treatments, configs })
    )
  } catch {
    // sessionStorage can throw in private browsing / quota exceeded.
  }
}

function resolveSdkKey(): string | undefined {
  return (
    process.env.NEXT_PUBLIC_SPLIT_IO_KEY ||
    process.env.SPLIT_IO_API_KEY ||
    undefined
  )
}

export function getReleasedTreatment(name: string): string | undefined {
  return releasedTreatments[name]
}

/**
 * Returns the released config JSON string for a 100%-rolled-out flag, or
 * undefined if the flag has no configuration in the splitChanges payload.
 * Callers should fall back to '{}' when this returns undefined.
 */
export function getReleasedTreatmentConfig(name: string): string | undefined {
  return releasedConfigs[name]
}

export function isRolloutPlanSettled(): boolean {
  return rolloutPlanSettled
}

/**
 * Subscribe to rollout-plan settlement. Listeners fire when the plan becomes
 * settled (network commit, non-OK, or error) — not on cache hydration alone.
 */
export function subscribeToRolloutPlanSettled(
  listener: SettlementListener
): () => void {
  settlementListeners.add(listener)

  return () => {
    settlementListeners.delete(listener)
  }
}

function markSettled(): void {
  rolloutPlanSettled = true
  settlementListeners.forEach((listener) => listener())
}

export function resetSplitRolloutPlanForTests(): void {
  releasedTreatments = {}
  releasedConfigs = {}
  rolloutPlanSettled = false
  inFlightFetch = null
  settlementListeners.clear()
}

function hydrateReleasedRollout(
  treatments: Record<string, string>,
  configs: Record<string, string>
): void {
  releasedTreatments = treatments
  releasedConfigs = configs
}

function commitReleasedRollout(
  treatments: Record<string, string>,
  configs: Record<string, string>
): void {
  releasedTreatments = treatments
  releasedConfigs = configs
  markSettled()
}

export async function fetchSplitRolloutPlan(): Promise<Record<string, string>> {
  if (inFlightFetch) {
    return inFlightFetch
  }

  const sdkKey = resolveSdkKey()

  if (!sdkKey) {
    commitReleasedRollout({}, {})

    return {}
  }

  inFlightFetch = (async () => {
    try {
      const response = await fetch(SPLIT_CHANGES_URL, {
        headers: {
          Authorization: `Bearer ${sdkKey}`,
          Accept: 'application/json',
        },
        signal: createTimeoutSignal(SPLIT_CHANGES_TIMEOUT_MS),
      })

      if (!response.ok) {
        // Keep any session-hydrated map; still mark settled so hooks stop.
        markSettled()

        return releasedTreatments
      }

      const payload = (await response.json()) as {
        splits?: SplitDefinition[]
        till?: number
      }
      const splits = payload.splits ?? []
      const treatments = parseReleasedTreatments(splits)
      const configs = parseReleasedConfigs(splits)

      commitReleasedRollout(treatments, configs)
      writeSessionCache(treatments, configs, payload.till)

      return treatments
    } catch {
      markSettled()

      return releasedTreatments
    } finally {
      inFlightFetch = null
    }
  })()

  return inFlightFetch
}

/**
 * Populate the released-treatment map if it is not already settled.
 * Hydrates from sessionStorage first so repeat visits resolve synchronously
 * before the network refresh.
 */
export async function ensureSplitRolloutPlan(): Promise<void> {
  if (rolloutPlanSettled || inFlightFetch) {
    if (inFlightFetch) {
      await inFlightFetch
    }

    return
  }

  const cached = readSessionCache()

  if (cached) {
    hydrateReleasedRollout(cached.treatments, cached.configs)
  }

  await fetchSplitRolloutPlan()
}
